/**
 * Helper functions for generating contextual hints
 * These provide reusable logic across different achievements
 */

import type { BlockLine } from '../../types/blocks';

/**
 * Check if the tw-block counts match the expected values in exact order
 */
export function hasExactBlockCounts(lines: BlockLine[], expectedCounts: number[]): boolean {
  if (lines.length !== expectedCounts.length) return false;
  return lines.every((line, index) => line.blocks === expectedCounts[index]);
}

/**
 * Check if the tw-block counts match expected values in any order
 */
export function hasBlockCountsInAnyOrder(lines: BlockLine[], expectedCounts: number[]): boolean {
  if (lines.length !== expectedCounts.length) return false;

  const actualCounts = lines.map(l => l.blocks).sort((a, b) => a - b);
  const sortedExpected = [...expectedCounts].sort((a, b) => a - b);

  return actualCounts.every((count, index) => count === sortedExpected[index]);
}

/**
 * Find a line by label (case-insensitive).
 * Accepts multiple alternative labels so callers can pass both singular and plural forms.
 */
export function findLineByLabel(lines: BlockLine[], ...labels: string[]): BlockLine | undefined {
  const normalized = labels.map(l => l.toLowerCase().trim());
  return lines.find(line => {
    const lineLabelNorm = line.label?.toLowerCase().trim();
    return lineLabelNorm !== undefined && normalized.includes(lineLabelNorm);
  });
}

/**
 * Check if a line has a label that contains a specific word (case-insensitive)
 */
export function lineHasLabelContaining(line: BlockLine, word: string): boolean {
  if (!line.label) return false;
  return line.label.toLowerCase().includes(word.toLowerCase());
}

/**
 * Count how many lines have non-empty labels
 */
export function countLabeledLines(lines: BlockLine[]): number {
  return lines.filter(line => line.label && line.label.trim().length > 0).length;
}

/**
 * Get the index of a line with a specific tw-block count
 */
export function findIndexWithBlockCount(lines: BlockLine[], blockCount: number): number {
  return lines.findIndex(line => line.blocks === blockCount);
}

/**
 * Check if exactly one line has the correct count
 */
export function hasExactlyOneCorrectValue(lines: BlockLine[], expectedCounts: number[]): boolean {
  if (lines.length !== expectedCounts.length) return false;

  let matchCount = 0;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].blocks === expectedCounts[i]) {
      matchCount++;
    }
  }

  return matchCount === 1;
}

/**
 * Get which line index has the correct value (returns -1 if none or multiple)
 */
export function getCorrectLineIndex(lines: BlockLine[], expectedCounts: number[]): number {
  if (lines.length !== expectedCounts.length) return -1;

  let correctIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].blocks === expectedCounts[i]) {
      if (correctIndex !== -1) return -1; // Multiple correct
      correctIndex = i;
    }
  }

  return correctIndex;
}

/**
 * Check if the arrangement matches expected
 */
export function hasCorrectArrangement(
  arrangement: 'parallel' | 'sequence',
  expected: 'parallel' | 'sequence',
): boolean {
  return arrangement === expected;
}

/**
 * Check if multiplier is at least the expected value
 */
export function hasEnoughMultiples(multiplier: number, expected: number): boolean {
  return multiplier >= expected;
}

/**
 * Format a ratio as a string (e.g., "1:2:3")
 */
export function formatRatio(counts: number[]): string {
  return counts.join(':');
}

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 */
export function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Describe which value needs adjustment (first, second, third, etc.)
 */
export function describeValuePosition(index: number): string {
  const positions = ['first', 'second', 'third', 'fourth', 'fifth'];
  return positions[index] || `${getOrdinal(index + 1)}`;
}

/**
 * Check if two ratios are mathematically equivalent (same proportions)
 * For example: [1, 2] is equivalent to [2, 4], [3, 6], etc.
 */
export function areRatiosEquivalent(ratio1: number[], ratio2: number[]): boolean {
  if (ratio1.length !== ratio2.length) return false;
  if (ratio1.length === 0) return true;

  // Find the GCD and reduce both ratios
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

  const reduceRatio = (ratio: number[]): number[] => {
    const divisor = ratio.reduce((acc, val) => gcd(acc, val));
    return divisor === 0 ? ratio : ratio.map(v => v / divisor);
  };

  const reduced1 = reduceRatio(ratio1);
  const reduced2 = reduceRatio(ratio2);

  return reduced1.every((val, idx) => val === reduced2[idx]);
}
